import { AdminLayout } from "@/components/admin/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, CheckCircle, AlertTriangle, XCircle, Activity, Download, Upload, Wrench } from "lucide-react";
import { useState } from "react";
import { format } from "date-fns";

const AudioPipeline = () => {
  const [isRunningCheck, setIsRunningCheck] = useState(false);

  // Fetch logs for last 24h
  const { data: logs, refetch: refetchLogs } = useQuery({
    queryKey: ["audio-pipeline-logs"],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from("audio_processing_logs")
        .select("*")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(50);
      return data || [];
    },
  });

  // Fetch system alerts
  const { data: alerts, refetch: refetchAlerts } = useQuery({
    queryKey: ["system-alerts-audio"],
    queryFn: async () => {
      const { data } = await supabase
        .from("system_alerts")
        .select("*")
        .eq("is_resolved", false)
        .order("created_at", { ascending: false })
        .limit(10);
      return data || [];
    },
  });

  // Stats from logs
  const stats = {
    total: logs?.length || 0,
    success: logs?.filter(l => l.status === "success").length || 0,
    errors: logs?.filter(l => l.status === "error").length || 0,
    downloads: logs?.filter(l => l.step === "download").length || 0,
    uploads: logs?.filter(l => l.step === "upload").length || 0,
    repairs: logs?.filter(l => l.step === "auto_repair").length || 0,
    repairSuccess: logs?.filter(l => l.step === "auto_repair" && l.status === "success").length || 0,
    healthChecks: logs?.filter(l => l.step === "health_check").length || 0,
  };

  const errorRate = stats.total > 0 ? (stats.errors / stats.total * 100) : 0;
  const systemStatus = errorRate > 5 ? "critical" : errorRate > 2 ? "warning" : "ok";

  const runHealthCheck = async () => {
    setIsRunningCheck(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      await fetch(`https://${projectId}.supabase.co/functions/v1/audio-health-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      setTimeout(() => {
        refetchLogs();
        refetchAlerts();
        setIsRunningCheck(false);
      }, 3000);
    } catch {
      setIsRunningCheck(false);
    }
  };

  const resolveAlert = async (id: string) => {
    await supabase.from("system_alerts").update({ is_resolved: true }).eq("id", id);
    refetchAlerts();
  };

  const stepIcon = (step: string) => {
    switch (step) {
      case "download": return <Download className="w-3.5 h-3.5" />;
      case "upload": return <Upload className="w-3.5 h-3.5" />;
      case "auto_repair": return <Wrench className="w-3.5 h-3.5" />;
      case "health_check": return <Activity className="w-3.5 h-3.5" />;
      default: return <Activity className="w-3.5 h-3.5" />;
    }
  };

  const statusBadge = (status: string) => {
    if (status === "success") return <Badge variant="default" className="bg-green-500/10 text-green-500 border-green-500/20">✓ Success</Badge>;
    if (status === "error") return <Badge variant="destructive">✗ Error</Badge>;
    return <Badge variant="secondary">{status}</Badge>;
  };

  return (
    <AdminLayout title="Audio Pipeline" description="Мониторинг и auto-repair аудио системы">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">🎧 Audio Pipeline</h1>
          <Button onClick={runHealthCheck} disabled={isRunningCheck} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${isRunningCheck ? "animate-spin" : ""}`} />
            {isRunningCheck ? "Проверяем..." : "Запустить проверку"}
          </Button>
        </div>

        {/* Active Alerts Banner */}
        {alerts && alerts.length > 0 && (
          <div className="space-y-2">
            {alerts.map((alert: any) => (
              <div key={alert.id} className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  <span className="text-sm font-medium text-destructive">{alert.message}</span>
                  <span className="text-xs text-muted-foreground">{format(new Date(alert.created_at), "dd.MM HH:mm")}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => resolveAlert(alert.id)}>
                  Закрыть
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* System Status + Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {/* System status card */}
          <Card className="col-span-2 md:col-span-1">
            <CardContent className="pt-4 pb-3 text-center">
              {systemStatus === "ok" && <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-1" />}
              {systemStatus === "warning" && <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-1" />}
              {systemStatus === "critical" && <XCircle className="w-8 h-8 text-destructive mx-auto mb-1" />}
              <p className="text-xs text-muted-foreground">Статус</p>
              <p className="text-sm font-bold uppercase">{systemStatus === "ok" ? "🟢 OK" : systemStatus === "warning" ? "🟡 Warning" : "🔴 Critical"}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Error rate: {errorRate.toFixed(1)}%</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold">{stats.success}</p>
              <p className="text-xs text-muted-foreground">Успешных</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-destructive">{stats.errors}</p>
              <p className="text-xs text-muted-foreground">Ошибок</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold">{stats.repairs}</p>
              <p className="text-xs text-muted-foreground">Auto-repair</p>
              <p className="text-[10px] text-green-500">{stats.repairSuccess} успешных</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold">{stats.healthChecks}</p>
              <p className="text-xs text-muted-foreground">Health checks</p>
            </CardContent>
          </Card>
        </div>

        {/* Logs Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Последние события (24ч)</CardTitle>
          </CardHeader>
          <CardContent>
            {!logs || logs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Нет событий за последние 24 часа</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">Время</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Gift Slug</TableHead>
                      <TableHead>HTTP</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead className="w-[60px]">Retry</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log: any) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-xs font-mono">{format(new Date(log.created_at), "dd.MM HH:mm:ss")}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-xs">
                            {stepIcon(log.step)}
                            {log.step}
                          </div>
                        </TableCell>
                        <TableCell>{statusBadge(log.status)}</TableCell>
                        <TableCell className="text-xs font-mono max-w-[120px] truncate">{log.gift_slug || "—"}</TableCell>
                        <TableCell className="text-xs">{log.http_status || "—"}</TableCell>
                        <TableCell className="text-xs">{log.file_size ? `${(log.file_size / 1024).toFixed(0)}KB` : "—"}</TableCell>
                        <TableCell className="text-xs text-center">{log.retry_count || 0}</TableCell>
                        <TableCell className="text-xs text-destructive max-w-[200px] truncate">{log.error_message || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AudioPipeline;
